// ===========================================
// PREMIUM ANALYTICS PAGE - VP-Level Design
// Matching Tripnovac Platform Hub Quality
// Comprehensive Marketing Analytics Dashboard
// ===========================================

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Mail,
  MousePointerClick,
  Eye,
  DollarSign,
  Calendar,
  ChevronDown,
  Download,
  RefreshCw,
  Target,
  Zap,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  PieChart,
  LineChart,
  Share2,
  MessageSquare,
  Megaphone,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Filter,
} from 'lucide-react';



// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string | number;
  change: number;
  changeLabel: string;
  icon: React.ReactNode;
  gradient: string;
  trend: 'up' | 'down';
}> = ({ title, value, change, changeLabel, icon, gradient, trend }) => (
  <motion.div
    whileHover={{ y: -4 }}
    className={`relative overflow-hidden rounded-2xl p-6 ${gradient} text-white shadow-xl`}
  >
    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/3" />
    <div className="relative z-10">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
          {icon}
        </div>
        <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
          trend === 'up' ? 'bg-white/20' : 'bg-red-500/30'
        }`}>
          {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {trend === 'up' ? '+' : ''}{change}%
        </div>
      </div>
      <h3 className="text-sm font-medium text-white/70">{title}</h3>
      <p className="text-3xl font-bold mt-1">{value}</p>
      <p className="text-xs text-white/60 mt-2">{changeLabel}</p>
    </div>
  </motion.div>
);

// Small Metric Card
const MetricCard: React.FC<{
  title: string;
  value: string;
  change: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, change, icon, color }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center text-white`}>
        {icon}
      </div>
      <span className={`flex items-center gap-1 text-xs font-bold ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
        {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {Math.abs(change)}%
      </span>
    </div>
    <h4 className="text-sm text-gray-500">{title}</h4>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
  </div>
);

// Simple Bar Chart Component (CSS-based for simplicity)
const SimpleBarChart: React.FC<{ data: Array<{ name: string; sent: number; opened: number; clicked: number }> }> = ({ data }) => {
  const maxValue = Math.max(...data.map(d => Math.max(d.sent, d.opened, d.clicked))) || 1; // Avoid division by zero
  
  return (
    <div className="flex items-end justify-between gap-2 h-48">
      {data.map((day, index) => (
        <div key={day.name} className="flex-1 flex flex-col items-center gap-2">
          <div className="w-full flex items-end justify-center gap-1 h-40">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${(day.sent / maxValue) * 100}%` }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="w-3 bg-violet-200 rounded-t"
              title={`Sent: ${day.sent}`}
            />
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${(day.opened / maxValue) * 100}%` }}
              transition={{ delay: index * 0.1 + 0.1, duration: 0.5 }}
              className="w-3 bg-violet-400 rounded-t"
              title={`Opened: ${day.opened}`}
            />
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${(day.clicked / maxValue) * 100}%` }}
              transition={{ delay: index * 0.1 + 0.2, duration: 0.5 }}
              className="w-3 bg-violet-600 rounded-t"
              title={`Clicked: ${day.clicked}`}
            />
          </div>
          <span className="text-xs text-gray-500">{day.name}</span>
        </div>
      ))}
    </div>
  );
};

// Simple Line Chart Component (CSS-based)
const SimpleLineChart: React.FC<{ data: Array<{ name: string; revenue: number }> }> = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="h-48 flex items-center justify-center text-gray-400">No data available</div>;
  }
  
  const maxValue = Math.max(...data.map(d => d.revenue));
  const minValue = Math.min(...data.map(d => d.revenue));
  const range = maxValue - minValue || 1; // Avoid division by zero
  const dataLen = data.length > 1 ? data.length - 1 : 1; // Avoid division by zero
  
  return (
    <div className="relative h-48">
      {/* Grid lines */}
      <div className="absolute inset-0 flex flex-col justify-between">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="border-t border-gray-100" />
        ))}
      </div>
      
      {/* Line chart using SVG */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 192" preserveAspectRatio="none">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgb(139, 92, 246)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="rgb(139, 92, 246)" stopOpacity="0" />
          </linearGradient>
        </defs>
        
        {/* Area fill */}
        <motion.path
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          d={`
            M 0 ${192 - ((data[0].revenue - minValue) / range) * 180}
            ${data.map((d, i) => {
              const x = (i / dataLen) * 100;
              const y = 192 - ((d.revenue - minValue) / range) * 180;
              return `L ${x} ${y}`;
            }).join(' ')}
            L 100 192
            L 0 192
            Z
          `}
          fill="url(#lineGradient)"
        />
        
        {/* Line */}
        <motion.path
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5 }}
          d={`
            M 0 ${192 - ((data[0].revenue - minValue) / range) * 180}
            ${data.map((d, i) => {
              const x = (i / dataLen) * 100;
              const y = 192 - ((d.revenue - minValue) / range) * 180;
              return `L ${x} ${y}`;
            }).join(' ')}
          `}
          fill="none"
          stroke="rgb(139, 92, 246)"
          strokeWidth="3"
          strokeLinecap="round"
        />
        
        {/* Data points */}
        {data.map((d, i) => {
          const x = i * (100 / dataLen);
          const y = 192 - ((d.revenue - minValue) / range) * 180;
          return (
            <motion.circle
              key={i}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 + i * 0.1 }}
              cx={`${x}%`}
              cy={y}
              r="5"
              fill="white"
              stroke="rgb(139, 92, 246)"
              strokeWidth="3"
            />
          );
        })}
      </svg>
      
      {/* X-axis labels */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-between translate-y-6">
        {data.map(d => (
          <span key={d.name} className="text-xs text-gray-500">{d.name}</span>
        ))}
      </div>
    </div>
  );
};

// Donut Chart Component
const DonutChart: React.FC<{
  segments: { label: string; value: number; color: string }[];
}> = ({ segments }) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  let currentAngle = 0;
  
  return (
    <div className="relative w-48 h-48 mx-auto">
      <svg viewBox="0 0 100 100" className="transform -rotate-90">
        {segments.map((segment, index) => {
          const angle = (segment.value / total) * 360;
          const startAngle = currentAngle;
          currentAngle += angle;
          
          const largeArc = angle > 180 ? 1 : 0;
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (currentAngle * Math.PI) / 180;
          
          const x1 = 50 + 40 * Math.cos(startRad);
          const y1 = 50 + 40 * Math.sin(startRad);
          const x2 = 50 + 40 * Math.cos(endRad);
          const y2 = 50 + 40 * Math.sin(endRad);
          
          return (
            <motion.path
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.2, duration: 0.5 }}
              d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={segment.color}
              className="hover:opacity-80 cursor-pointer transition-opacity"
            />
          );
        })}
        {/* Inner circle for donut effect */}
        <circle cx="50" cy="50" r="25" fill="white" />
      </svg>
      
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-2xl font-bold text-gray-900">{total.toLocaleString()}</p>
        <p className="text-xs text-gray-500">Total</p>
      </div>
    </div>
  );
};

const PremiumAnalyticsPage: React.FC = () => {
  const [dateRange, setDateRange] = useState('7d');
  const [loading, setLoading] = useState(true);

  // Real API data states
  const [dashMetrics, setDashMetrics] = useState<any>(null);
  const [emailPerfData, setEmailPerfData] = useState<Array<{ name: string; sent: number; opened: number; clicked: number }>>([]);
  const [revData, setRevData] = useState<Array<{ name: string; revenue: number }>>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [topCampaignList, setTopCampaignList] = useState<any[]>([]);

  const getDaysBack = (range: string) => {
    switch (range) { case '24h': return 1; case '7d': return 7; case '30d': return 30; case '90d': return 90; default: return 7; }
  };

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const daysBack = getDaysBack(dateRange);
      const endDate = new Date();
      const startDate = new Date(endDate.getTime() - daysBack * 24 * 60 * 60 * 1000);
      const isoStart = startDate.toISOString();
      const isoEnd = endDate.toISOString();

      const [dashRes, emailPerfRes, channelsRes, sourcesRes, campaignsRes, invoicesRes] = await Promise.allSettled([
        api.get<any>('/api/analytics/dashboard'),
        api.get<any>(`/api/analytics/email/performance?startDate=${isoStart}&endDate=${isoEnd}`),
        api.get<any>('/api/analytics/channels'),
        api.get<any>('/api/analytics/leads/sources'),
        api.get<any>('/api/campaigns?limit=5&sortBy=createdAt&sortOrder=desc'),
        api.get<any>('/api/invoices?page=1&limit=200'),
      ]);

      // Dashboard metrics
      if (dashRes.status === 'fulfilled') setDashMetrics(dashRes.value ?? null);

      // Email performance chart — last N days
      if (emailPerfRes.status === 'fulfilled') {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const perf: Array<{ date: string; sent: number; opened: number; clicked: number }> =
          (emailPerfRes.value as any)?.performance ?? [];
        const last7 = perf.slice(-7);
        if (last7.length > 0) {
          setEmailPerfData(last7.map(d => ({
            name: dayNames[new Date(d.date + 'T12:00:00').getDay()],
            sent: d.sent,
            opened: d.opened,
            clicked: d.clicked,
          })));
        }
      }

      // Channel metrics
      if (channelsRes.status === 'fulfilled') setChannels((channelsRes.value as any)?.channels ?? []);

      // Lead sources for donut chart
      if (sourcesRes.status === 'fulfilled') setSources((sourcesRes.value as any)?.sources ?? []);

      // Top campaigns
      if (campaignsRes.status === 'fulfilled') {
        setTopCampaignList((campaignsRes.value as any)?.campaigns ?? []);
      }

      // Revenue chart — group invoices by month (last 6 months)
      if (invoicesRes.status === 'fulfilled') {
        const invoiceList = (invoicesRes.value as any)?.invoices ?? [];
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const monthlyRevenue: Record<string, number> = {};
        invoiceList.forEach((inv: any) => {
          const d = new Date(inv.issueDate || inv.createdAt || Date.now());
          const key = `${d.getFullYear()}-${d.getMonth()}`;
          monthlyRevenue[key] = (monthlyRevenue[key] || 0) + (inv.totalAmount || inv.total || 0);
        });
        const revRows: Array<{ name: string; revenue: number }> = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(endDate.getFullYear(), endDate.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${d.getMonth()}`;
          revRows.push({ name: monthNames[d.getMonth()], revenue: monthlyRevenue[key] || 0 });
        }
        setRevData(revRows);
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // ---- Derived display data ----
  const fmtMoney = (n: number) =>
    n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(1)}K`
    : `$${n.toFixed(0)}`;

  const mainStats = [
    {
      title: 'Total Revenue',
      value: loading ? '...' : dashMetrics ? fmtMoney(dashMetrics.revenue?.total ?? 0) : '$0',
      change: Math.round(dashMetrics?.revenue?.growthRate ?? 0),
      changeLabel: 'vs last period',
      icon: <DollarSign className="w-6 h-6" />,
      gradient: 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500',
      trend: (dashMetrics?.revenue?.growthRate ?? 0) >= 0 ? 'up' as const : 'down' as const,
    },
    {
      title: 'Email Open Rate',
      value: loading ? '...' : dashMetrics ? `${(dashMetrics.campaigns?.avgOpenRate ?? 0).toFixed(1)}%` : '0%',
      change: 0,
      changeLabel: 'Industry avg: 21%',
      icon: <Eye className="w-6 h-6" />,
      gradient: 'bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500',
      trend: 'up' as const,
    },
    {
      title: 'Click-Through Rate',
      value: loading ? '...' : dashMetrics ? `${(dashMetrics.campaigns?.avgClickRate ?? 0).toFixed(1)}%` : '0%',
      change: 0,
      changeLabel: 'Industry avg: 2.5%',
      icon: <MousePointerClick className="w-6 h-6" />,
      gradient: 'bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500',
      trend: 'up' as const,
    },
    {
      title: 'New Contacts',
      value: loading ? '...' : dashMetrics ? (dashMetrics.contacts?.newThisMonth ?? 0).toLocaleString() : '0',
      change: Math.round(dashMetrics?.contacts?.growthRate ?? 0),
      changeLabel: 'vs last period',
      icon: <Users className="w-6 h-6" />,
      gradient: 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-500',
      trend: (dashMetrics?.contacts?.growthRate ?? 0) >= 0 ? 'up' as const : 'down' as const,
    },
  ];

  const CHANNEL_ICONS: Record<string, React.ReactNode> = {
    EMAIL: <Mail className="w-5 h-5" />, SMS: <MessageSquare className="w-5 h-5" />,
    WHATSAPP: <MessageSquare className="w-5 h-5" />, SOCIAL: <Share2 className="w-5 h-5" />,
    PUSH: <Globe className="w-5 h-5" />,
  };
  const CHANNEL_COLORS: Record<string, string> = {
    EMAIL: 'bg-blue-500', SMS: 'bg-emerald-500', WHATSAPP: 'bg-green-600',
    SOCIAL: 'bg-violet-500', PUSH: 'bg-amber-500',
  };

  const channelMetrics = channels.length > 0
    ? channels.slice(0, 4).map((ch: any) => ({
        title: `${ch.channel.charAt(0)}${ch.channel.slice(1).toLowerCase()} Campaigns`,
        value: (ch.sent || 0).toLocaleString(),
        change: 0,
        icon: CHANNEL_ICONS[ch.channel] ?? <Megaphone className="w-5 h-5" />,
        color: CHANNEL_COLORS[ch.channel] ?? 'bg-gray-500',
      }))
    : [
        { title: 'Emails Sent', value: (dashMetrics?.emails?.sent ?? 0).toLocaleString(), change: 0, icon: <Mail className="w-5 h-5" />, color: 'bg-blue-500' },
        { title: 'Total Campaigns', value: (dashMetrics?.campaigns?.total ?? 0).toLocaleString(), change: 0, icon: <Megaphone className="w-5 h-5" />, color: 'bg-violet-500' },
        { title: 'Active Campaigns', value: (dashMetrics?.campaigns?.active ?? 0).toLocaleString(), change: 0, icon: <Activity className="w-5 h-5" />, color: 'bg-emerald-500' },
        { title: 'Total Contacts', value: (dashMetrics?.contacts?.total ?? 0).toLocaleString(), change: Math.round(dashMetrics?.contacts?.growthRate ?? 0), icon: <Users className="w-5 h-5" />, color: 'bg-amber-500' },
      ];

  const SOURCE_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
  const trafficSources = sources.length > 0
    ? sources.map((s: any, i: number) => ({ label: s.source || 'Direct', value: s.count, color: SOURCE_COLORS[i % SOURCE_COLORS.length] }))
    : [{ label: 'No data yet', value: 1, color: '#e5e7eb' }];

  const topCampaigns = topCampaignList.map((c: any) => {
    const m = (c.metrics as any) || {};
    const sent = m.sent || 0;
    const opened = m.opened || 0;
    const clicked = m.clicked || 0;
    const rate = sent > 0 ? Math.round((opened / sent) * 1000) / 10 : 0;
    return { name: c.name, sent, opened, clicked, rate };
  });

  const chartEmailData = emailPerfData.length > 0 ? emailPerfData : [
    { name: 'Mon', sent: 0, opened: 0, clicked: 0 }, { name: 'Tue', sent: 0, opened: 0, clicked: 0 },
    { name: 'Wed', sent: 0, opened: 0, clicked: 0 }, { name: 'Thu', sent: 0, opened: 0, clicked: 0 },
    { name: 'Fri', sent: 0, opened: 0, clicked: 0 }, { name: 'Sat', sent: 0, opened: 0, clicked: 0 },
    { name: 'Sun', sent: 0, opened: 0, clicked: 0 },
  ];

  const chartRevData = revData.length > 0 ? revData : [
    { name: 'Jan', revenue: 0 }, { name: 'Feb', revenue: 0 }, { name: 'Mar', revenue: 0 },
    { name: 'Apr', revenue: 0 }, { name: 'May', revenue: 0 }, { name: 'Jun', revenue: 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/25">
              <BarChart3 className="w-6 h-6" />
            </div>
            Analytics
          </h1>
          <p className="text-gray-600 mt-2">
            Track your marketing performance and key metrics
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <div className="flex items-center bg-gray-100 rounded-xl p-1">
            {['24h', '7d', '30d', '90d'].map(range => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === range 
                    ? 'bg-white shadow text-violet-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {range}
              </button>
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => fetchAnalytics()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const csv = ['Metric,Value'];
              mainStats.forEach(s => csv.push(`${s.title},${s.value}`));
              const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `analytics-report-${new Date().toISOString().slice(0,10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium shadow-lg shadow-violet-500/25"
          >
            <Download className="w-4 h-4" />
            Export Report
          </motion.button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {mainStats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StatCard {...stat} />
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Performance Chart */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Email Performance</h2>
              <p className="text-sm text-gray-500">Last 7 days</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-violet-200"></span>
                Sent
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-violet-400"></span>
                Opened
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-violet-600"></span>
                Clicked
              </span>
            </div>
          </div>
          <SimpleBarChart data={chartEmailData} />
        </div>

        {/* Revenue Trend Chart */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Revenue Trend</h2>
              <p className="text-sm text-gray-500">Last 6 months</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
              <TrendingUp className="w-3 h-3" />
              +23% YoY
            </div>
          </div>
          <div className="pb-8">
            <SimpleLineChart data={chartRevData} />
          </div>
        </div>
      </div>

      {/* Channel Metrics & Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Channel Metrics */}
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
          {channelMetrics.map((metric, index) => (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <MetricCard {...metric} />
            </motion.div>
          ))}
        </div>

        {/* Traffic Sources */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Traffic Sources</h2>
          <DonutChart segments={trafficSources} />
          <div className="mt-6 space-y-2">
            {trafficSources.map(source => (
              <div key={source.label} className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: source.color }}></span>
                  {source.label}
                </span>
                <span className="text-sm font-medium text-gray-900">{source.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Campaigns Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Top Performing Campaigns</h2>
              <p className="text-sm text-gray-500">Based on engagement metrics</p>
            </div>
            <button onClick={() => window.location.href = '/campaigns'} className="text-sm text-violet-600 hover:text-violet-700 font-medium">
              View All Campaigns
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Campaign</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Sent</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Opened</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Clicked</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Open Rate</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topCampaigns.map((campaign, index) => (
                <motion.tr
                  key={campaign.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white">
                        <Mail className="w-5 h-5" />
                      </div>
                      <span className="font-medium text-gray-900">{campaign.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center text-gray-700">{campaign.sent.toLocaleString()}</td>
                  <td className="px-6 py-4 text-center text-gray-700">{campaign.opened.toLocaleString()}</td>
                  <td className="px-6 py-4 text-center text-gray-700">{campaign.clicked.toLocaleString()}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      campaign.rate >= 70 ? 'bg-emerald-100 text-emerald-700' :
                      campaign.rate >= 50 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {campaign.rate}%
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-full max-w-[120px] mx-auto">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${campaign.rate}%` }}
                          transition={{ duration: 1, delay: index * 0.2 }}
                          className={`h-full rounded-full ${
                            campaign.rate >= 70 ? 'bg-emerald-500' :
                            campaign.rate >= 50 ? 'bg-amber-500' :
                            'bg-red-500'
                          }`}
                        />
                      </div>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Key Insights - Dynamic based on real data */}
        <div className="lg:col-span-2 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl border border-violet-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <Zap className="w-5 h-5 text-violet-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">AI-Powered Insights</h2>
          </div>
          <div className="space-y-4">
            {/* Generate real insights based on actual data */}
            {(() => {
              const insights: Array<{ icon: React.ReactNode; text: string; type: string }> = [];
              const avgOpenRate = dashMetrics?.campaigns?.avgOpenRate ?? 0;
              const totalContacts = dashMetrics?.contacts?.total ?? 0;
              const contactGrowth = dashMetrics?.contacts?.growthRate ?? 0;
              const totalCampaigns = dashMetrics?.campaigns?.total ?? 0;
              
              if (avgOpenRate > 21) {
                insights.push({
                  icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
                  text: `Your email open rate of ${avgOpenRate.toFixed(1)}% exceeds the industry average of 21%. Great work!`,
                  type: 'success'
                });
              } else if (avgOpenRate > 0) {
                insights.push({
                  icon: <AlertCircle className="w-5 h-5 text-amber-600" />,
                  text: `Your email open rate is ${avgOpenRate.toFixed(1)}%. Try A/B testing subject lines to improve.`,
                  type: 'warning'
                });
              }
              
              if (contactGrowth > 10) {
                insights.push({
                  icon: <TrendingUp className="w-5 h-5 text-emerald-600" />,
                  text: `Your contact list grew by ${contactGrowth.toFixed(0)}% this period. Keep building your audience!`,
                  type: 'success'
                });
              } else if (contactGrowth < 0) {
                insights.push({
                  icon: <AlertCircle className="w-5 h-5 text-amber-600" />,
                  text: 'Your contact list declined this period. Consider running a lead generation campaign.',
                  type: 'warning'
                });
              }
              
              if (totalCampaigns === 0) {
                insights.push({
                  icon: <Target className="w-5 h-5 text-blue-600" />,
                  text: 'No campaigns sent yet. Create your first campaign to start engaging your audience.',
                  type: 'info'
                });
              }
              
              if (insights.length === 0) {
                insights.push({
                  icon: <Zap className="w-5 h-5 text-violet-600" />,
                  text: 'Send campaigns and grow your contact list to unlock personalized AI insights.',
                  type: 'info'
                });
              }
              
              return insights.map((insight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-violet-100"
                >
                  {insight.icon}
                  <p className="text-sm text-gray-700">{insight.text}</p>
                </motion.div>
              ));
            })()}
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-4 text-center py-8">
            <Mail className="w-12 h-12 text-gray-300 mx-auto" />
            <p className="text-gray-500 font-medium">No recent activity</p>
            <p className="text-sm text-gray-400">Activity from your campaigns will appear here</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumAnalyticsPage;
